/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Notification Types
 * ============================================================================
 *
 * Types for the extensible notification system.
 * These types support dynamic notification providers and role-based filtering.
 *
 * ============================================================================
 */

/**
 * Notification category.
 * - ATTENTION: Items requiring user action
 * - ACTIVITY: Recent events for awareness
 * - ALERT: System alerts and warnings
 */
export type NotificationCategory = 'ATTENTION' | 'ACTIVITY' | 'ALERT';

/**
 * Urgency level for attention items.
 */
export type NotificationUrgency = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * A single notification item from the backend.
 * Supports dynamic types - not restricted to a fixed set.
 */
export interface NotificationItem {
  /** Unique identifier */
  id: string;

  /** ID of the provider that generated this notification */
  providerId: string;

  /** Category of the notification */
  category: NotificationCategory;

  /** Dynamic type string (e.g., "PENDING_REGISTRATION", "FAILED_PAYMENT") */
  type: string;

  /** Title - can be a translation key or plain text */
  title: string;

  /** Description - can be a translation key or plain text */
  description: string;

  /** Count for aggregated items (e.g., "5 pending registrations") */
  count?: number;

  /** Urgency level for attention items */
  urgency?: NotificationUrgency;

  /** ID of the related entity */
  entityId?: string;

  /** Display name of the related entity */
  entityName?: string;

  /** Frontend route to navigate when clicked */
  route?: string;

  /** Icon identifier (Lucide icon name, e.g., "FileText", "AlertCircle") */
  icon?: string;

  /** Color code for styling (e.g., "#3B82F6") */
  color?: string;

  /** Flexible metadata for custom data */
  meta?: Record<string, unknown>;

  /** Timestamp when this notification was created */
  createdAt: string;
}

/**
 * Aggregated notification summary from the backend.
 */
export interface NotificationSummary {
  /** Items requiring user attention/action */
  attentionItems: NotificationItem[];

  /** Recent activity items */
  activityItems: NotificationItem[];

  /** Total count for the notification badge */
  totalCount: number;
}

/**
 * Empty notification summary for fallback/loading states.
 */
export const emptyNotificationSummary: NotificationSummary = {
  attentionItems: [],
  activityItems: [],
  totalCount: 0,
};
